import React from 'react';
import {useParams, useHistory} from 'react-router-dom';
import {yupResolver} from '@hookform/resolvers/yup';
import {Form, useCreateFormPostMutation} from 'api';
import {useForm, FormProvider} from 'react-hook-form';
import Block from './Block';
import validationSchema from './validationSchema';
import FormStyleContext from 'components/FormStyleContext';
import FormStyleContainer from 'components/FormStyleContainer';

interface Props {
  form: Form;
}

export default ({form}: Props) => {
  const history = useHistory();
  const {slug} = useParams<{
    slug: string;
  }>();

  const formPage = React.useMemo(
    () => form.formPages.find((formPage) => formPage.slug === slug),
    [form, slug],
  );

  const [createFormPost] = useCreateFormPostMutation({
    onCompleted: ({
      createFormPost: {
        formPost: {redirectUrl},
      },
    }) => {
      if (redirectUrl) {
        window.top.location.href = redirectUrl;
      } else {
        history.push(`/forms/${form.uuid}/pages/done`);
      }
    },
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema(formPage)),
    defaultValues: {},
    mode: 'onBlur',
  });

  const onSubmit = React.useCallback(
    (values: any) => {
      const params = Object.keys(values).map((key) => {
        const value = values[key];
        return {
          form_block_id: key,
          value,
        };
      });

      createFormPost({
        variables: {
          uuid: form.uuid,
          params,
        },
      });
    },
    [form, createFormPost],
  );

  return (
    <div className="w-full flex items-center justify-center py-12 px-4">
      <FormStyleContainer
        className="max-w-full w-[480px] flex flex-col gap-8"
        form={form}>
        <div className="flex flex-col items-center gap-4">
          {form.formLogoFile && (
            <img
              src={form.formLogoFile}
              className="max-w-full mb-4"
              style={{
                width: `${form.formLogoWidth * form.formLogoScale}px`,
              }}
            />
          )}
          <h1 className="text-lg font-bold text-center leading-normal">
            {form.formTitle}
          </h1>
        </div>
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
                e.preventDefault();
              }
            }}
            className="w-full flex flex-col gap-8">
            {formPage?.formBlocks.map((block) => (
              <Block key={block.id} block={block} />
            ))}
          </form>
        </FormProvider>
      </FormStyleContainer>
    </div>
  );
};
